import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, FormEventHandler, MouseEventHandler, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChooseFileButton } from 'src/components/Button/ChooseFileButton';
import { UploadButton } from 'src/components/Button/UploadButton';
import { FormTextField, RequiredSpan } from 'src/components/TextInput/FormTextField';
import { LightTooltip } from './components/LightTooltip';
import { ProcessingStatus } from './components/ProcessingStatus';
import { FileInputContainer, FileInputField, VisuallyHiddenInput } from './components/styles';

export type FileUploaderProps = {
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  percentage?: number;
  loading: boolean;
  status?: 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  uploadedFileName?: string;
  runID?: string;
  onUpload?: FormEventHandler<HTMLFormElement>;
  label?: ReactNode;
  textOnComplete?: string;
};

export function FileUploader({
  value,
  onChange,
  loading,
  status,
  uploadedFileName,
  runID,
  percentage,
  onUpload,
  label,
  textOnComplete,
}: Readonly<FileUploaderProps>) {
  const { t } = useTranslation('common');

  const onInputClick: MouseEventHandler<HTMLInputElement> = (event) => {
    (event.target as any).value = '';
  };

  const isIngesting = status === 'IN_PROGRESS';
  const showPercentageTooltip = !!percentage && /* istanbul ignore next */ percentage < 100;

  let buttonText;

  if (loading) {
    buttonText = isIngesting ? t('fileUpload.processingStatus.processing') : t('fileUpload.processingStatus.uploading');
  } else {
    buttonText = t('fileUpload.upload');
  }

  return (
    <FileInputContainer data-testid="upload-file-section" onSubmit={onUpload}>
      <FileInputField>
        <FormTextField
          variant="filled"
          type="text"
          placeholder={t('fileUpload.placeholder')}
          data-testid="file-input"
          value={value}
          fullWidth
          required
          disabled
          label={label ?? <RequiredSpan>(required)</RequiredSpan>}
          boldLabel
        />
        <ChooseFileButton
          disabled={isIngesting}
          component="label"
          variant="contained"
          size="large"
          data-testid="upload-browse-button"
        >
          {t('fileUpload.browse')}
          <VisuallyHiddenInput
            type="file"
            accept=".csv"
            data-testid="hidden-file-input"
            onChange={onChange}
            onClick={onInputClick}
          />
        </ChooseFileButton>
      </FileInputField>
      <LightTooltip open={showPercentageTooltip} arrow title={t('fileUpload.percentUploaded', { percentage })}>
        <UploadButton
          loading={loading}
          disabled={!value}
          startIcon={<FontAwesomeIcon icon={faArrowUpFromBracket} size="3x" />}
        >
          {buttonText}
        </UploadButton>
      </LightTooltip>
      {status && (
        <ProcessingStatus
          status={status}
          fileName={uploadedFileName ?? /* istanbul ignore next */ ''}
          runID={runID ?? /* istanbul ignore next */ ''}
          textOnComplete={textOnComplete}
        />
      )}
    </FileInputContainer>
  );
}
